import React, { memo, useEffect, useMemo, useRef } from 'react'
import { Box, useTheme } from '@mui/material'
import {
    ColorType,
    createChart,
    CrosshairMode,
    IChartApi,
    LineData,
    Time,
    UTCTimestamp
} from 'lightweight-charts'
import merge from 'lodash/merge'
import { CHART_PRICE_SCALE_MARGINS, chartLayoutOptions } from '../../assets/constants'

export type ChartSkeletonPlaceholderProps = {
    hideTimescale?: boolean
    hideLeftScale?: boolean
    hideRightScale?: boolean
    showLeftPriceScale?: boolean
    showRightPriceScale?: boolean
    screenerChart?: boolean
    hideGrid?: boolean
}

const SECONDS_PER_BAR = 86400

/** Realistic sample close prices for the loading skeleton area series. */
const SKELETON_VALUES: readonly number[] = [
    23403, 23391.15, 23383.15, 23398.55, 23399.35, 23396.8, 23400.95, 23389.15, 23408.8, 23402.4,
    23400.65, 23391.3, 23388.85, 23376, 23373.1, 23375.8, 23362.85, 23377.45, 23384.95, 23387.9,
    23378.4, 23387.15, 23368.45, 23356.1, 23358.4, 23363.85, 23353.3, 23373.7, 23378.35, 23370.75,
    23367.15, 23365.75, 23356.2, 23348.05, 23342.65, 23333.9, 23339.3, 23332.75, 23324.8, 23325.35,
    23330.75, 23328.3, 23319.9, 23322.95, 23340.8, 23343.8, 23347.9, 23360.35, 23357.25, 23361.1,
    23361.6, 23359.8, 23344.05, 23348.3, 23350.75, 23356.15, 23357.8, 23352.4, 23360.1, 23362.3,
    23357.2, 23375.15, 23382.65, 23385.95, 23385, 23383.25, 23384.1, 23375.85, 23383.85, 23382.15,
    23384.1, 23384.55, 23382.15, 23379.8, 23379.7, 23372.6, 23359, 23360.25, 23376.05, 23414,
    23418, 23431.55, 23438, 23432.2, 23429, 23430.3, 23427.25, 23426.25, 23405.85, 23417.8,
    23413.9, 23405.45, 23394.75, 23406.2, 23408.15, 23418.95, 23425.6, 23441.15, 23436.2, 23443.55,
    23451, 23457.65, 23452.9, 23460.05, 23465.05, 23461.85, 23458.75, 23461.15, 23461.5, 23455.25,
    23463.2, 23468.3, 23459.1, 23467.05, 23477.1, 23474.2, 23465.85, 23460.95, 23474.55, 23470.2,
    23474.9, 23483.5, 23483.9, 23483.85, 23494.3, 23485.95, 23478.35, 23503.5, 23494.55, 23516.4,
    23509.9, 23498.3, 23501.1, 23499.8, 23495.8, 23504.95
]

const buildSkeletonLineData = (baseTime: number): LineData<Time>[] =>
    SKELETON_VALUES.map((value, i) => ({
        time: (baseTime + i * SECONDS_PER_BAR) as UTCTimestamp,
        value
    }))

/**
 * Static lightweight-charts placeholder: area series with visible time and price scales.
 */
const ChartSkeletonPlaceholder: React.FC<ChartSkeletonPlaceholderProps> = ({
    hideTimescale = false,
    hideLeftScale,
    hideRightScale = false,
    showLeftPriceScale = false,
    showRightPriceScale = true,
    screenerChart = false,
    hideGrid = false
}) => {
    const theme = useTheme()
    const containerRef = useRef<HTMLDivElement>(null)
    const chartRef = useRef<IChartApi | null>(null)

    const skeletonSeriesColors = useMemo(() => {
        const isDark = theme.palette.mode === 'dark'
        return {
            line: isDark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.18)',
            top: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.07)',
            bottom: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)'
        }
    }, [theme.palette.mode])

    const scaleTextColor = useMemo(() => {
        const isDark = theme.palette.mode === 'dark'
        return isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)'
    }, [theme.palette.mode])

    const chartOptions = useMemo(() => {
        const gridColor = '#88888815'
        const scaleBorder = theme.palette.divider
        const rightVisible = !hideRightScale && showRightPriceScale
        const leftVisible = !hideLeftScale && showLeftPriceScale
        const timeScaleVisible = !hideTimescale

        return merge({}, chartLayoutOptions, {
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                textColor: scaleTextColor,
                fontSize: 12
            },
            grid: {
                horzLines: {
                    visible: screenerChart ? false : !hideGrid,
                    color: gridColor
                },
                vertLines: {
                    visible: screenerChart ? false : !hideGrid,
                    color: gridColor
                }
            },
            timeScale: {
                visible: timeScaleVisible,
                borderColor: scaleBorder,
                borderVisible: true,
                ticksVisible: true,
                timeVisible: false,
                fixLeftEdge: false,
                fixRightEdge: false
            },
            rightPriceScale: {
                visible: rightVisible,
                borderColor: scaleBorder,
                borderVisible: true,
                alignLabels: true,
                ticksVisible: true,
                textColor: scaleTextColor,
                scaleMargins: CHART_PRICE_SCALE_MARGINS
            },
            leftPriceScale: {
                visible: leftVisible,
                borderColor: scaleBorder,
                borderVisible: true,
                alignLabels: true,
                ticksVisible: true,
                textColor: scaleTextColor,
                scaleMargins: CHART_PRICE_SCALE_MARGINS
            },
            crosshair: {
                mode: CrosshairMode.Hidden,
                vertLine: { visible: false },
                horzLine: { visible: false }
            },
            handleScroll: false,
            handleScale: false
        })
    }, [
        theme.palette.background.paper,
        theme.palette.divider,
        scaleTextColor,
        hideTimescale,
        hideLeftScale,
        hideRightScale,
        showLeftPriceScale,
        showRightPriceScale,
        screenerChart,
        hideGrid
    ])

    const skeletonLineData = useMemo(() => {
        const baseTime = Math.floor(Date.now() / 1000) - SKELETON_VALUES.length * SECONDS_PER_BAR
        return buildSkeletonLineData(baseTime)
    }, [])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const chart = createChart(container, {
            ...chartOptions,
            autoSize: true
        })

        const series = chart.addAreaSeries({
            lineColor: skeletonSeriesColors.line,
            topColor: skeletonSeriesColors.top,
            bottomColor: skeletonSeriesColors.bottom,
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false
        })

        series.priceScale().applyOptions({ scaleMargins: CHART_PRICE_SCALE_MARGINS })

        series.setData(skeletonLineData)
        chart.timeScale().fitContent()

        chartRef.current = chart

        return () => {
            chart.remove()
            chartRef.current = null
        }
    }, [chartOptions, skeletonLineData, skeletonSeriesColors])

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                pointerEvents: 'none'
            }}
        />
    )
}

export default memo(ChartSkeletonPlaceholder)
